import React from "react";
import LegalLayout, { LegalSection, LegalList, CONTACT_EMAIL, ENTITY_NAME } from "@/components/legal/LegalLayout";

export default function PrivacyPolicyPage() {
    return (
        <LegalLayout
            title="Privacy Policy"
            subtitle={`How ${ENTITY_NAME} collects, uses, and protects your information — plus our commitments to preventing deception and device abuse, and to providing a safe, secure environment for our users.`}
            testId="privacy-policy-page"
        >
            <LegalSection id="overview" title="1. Overview">
                <p>
                    This Privacy Policy describes how {ENTITY_NAME} ("we," "us," or "our") collects, uses, and discloses personal information when you visit www.golfpowerplay.com (the "Site"), join our launch waitlist, or otherwise interact with our pre-launch services (collectively, the "Services").
                </p>
                <p>
                    By using the Services, you agree to the practices described in this policy. If you do not agree, please do not use the Services.
                </p>
            </LegalSection>

            <LegalSection id="info-we-collect" title="2. Information We Collect">
                <p><strong>Information you provide directly:</strong></p>
                <LegalList
                    items={[
                        "Email address when you join the launch waitlist.",
                        "Preferred mobile platform (iOS, Android, or either).",
                        "Any other information you voluntarily provide (e.g., feedback, support inquiries).",
                    ]}
                />
                <p><strong>Information collected automatically:</strong></p>
                <LegalList
                    items={[
                        "Device and browser information (user agent, operating system, screen size).",
                        "Approximate location derived from IP address (country / region level).",
                        "Site usage data (pages visited, time on page, referring website).",
                        "Cookies and similar technologies — see the Cookies section below.",
                    ]}
                />
                <p>
                    We do <strong>not</strong> knowingly collect Social Security numbers, government IDs, payment-card numbers, precise GPS location, or other categories of sensitive personal information through the pre-launch Site.
                </p>
            </LegalSection>

            <LegalSection id="how-we-use" title="3. How We Use Your Information">
                <p>We use the information we collect to:</p>
                <LegalList
                    items={[
                        "Send you launch announcements, early-access invites, and bonus-coin offers you opted into by joining the waitlist.",
                        "Operate, maintain, secure, and improve the Site and Services.",
                        "Detect, investigate, and prevent fraud, abuse, and security incidents.",
                        "Comply with legal obligations and enforce our Terms of Service.",
                        "Conduct internal analytics to understand how visitors find and use the Site.",
                    ]}
                />
                <p>
                    We do not use your personal information for automated decisions that produce legal or similarly significant effects, and we do not sell personal information for money.
                </p>
            </LegalSection>

            <LegalSection id="legal-bases" title="4. Legal Bases for Processing (EU/UK Visitors)">
                <p>If you are located in the European Economic Area or United Kingdom, we rely on the following legal bases under GDPR / UK GDPR:</p>
                <LegalList
                    items={[
                        "Consent — for marketing emails about our app launch (you may withdraw consent at any time by unsubscribing).",
                        "Legitimate interests — for site security, fraud prevention, and aggregate analytics.",
                        "Legal obligation — to comply with applicable laws and regulatory requests.",
                    ]}
                />
            </LegalSection>

            <LegalSection id="cookies" title="5. Cookies & Similar Technologies">
                <p>
                    We use a small number of cookies and similar technologies to operate the Site and remember your preferences (such as your selected language and whether you have dismissed the cookie banner). Where required, we present a consent banner that lets you accept or reject optional analytics cookies.
                </p>
                <p>
                    You can also control cookies through your browser settings. Blocking strictly necessary cookies may break parts of the Site.
                </p>
            </LegalSection>

            <LegalSection id="sharing" title="6. How We Share Information">
                <p>We share personal information only as described below:</p>
                <LegalList
                    items={[
                        "Service providers — third parties that host our infrastructure, send transactional email, or provide analytics, all under contractual confidentiality and data-protection obligations.",
                        "Legal & safety — to comply with subpoenas, court orders, or other lawful requests; to enforce our Terms of Service; or to protect the rights, property, or safety of Golf Power Play, our users, or the public.",
                        "Business transfers — in connection with a merger, acquisition, financing, reorganization, or sale of assets, in which case we will use reasonable efforts to ensure the recipient honors this Privacy Policy.",
                    ]}
                />
                <p>
                    We do not sell personal information for monetary consideration, and we do not share personal information for cross-context behavioral advertising under the CCPA / CPRA.
                </p>
            </LegalSection>

            <LegalSection id="your-rights" title="7. Your Privacy Rights">
                <p>Depending on where you live, you may have the following rights regarding your personal information:</p>
                <LegalList
                    items={[
                        "Access — request a copy of the personal information we hold about you.",
                        "Correction — request correction of inaccurate or incomplete information.",
                        "Deletion — request that we delete personal information we hold about you.",
                        "Portability — request a portable copy in a commonly used format.",
                        "Opt-out of sale or sharing — see the Do Not Sell or Share page (California residents).",
                        "Withdraw consent — for any processing that relies on your consent.",
                        "Lodge a complaint — with your local data protection authority (EU/UK residents).",
                    ]}
                />
                <p>
                    To exercise any of these rights, email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0B4A34] underline">{CONTACT_EMAIL}</a>. We will respond within the time frames required by applicable law (generally 45 days under the CCPA, with one 45-day extension when needed).
                </p>
                <p>
                    We will not discriminate against you for exercising any of your privacy rights — your access to our Services and any future bonuses tied to waitlist signup will remain intact.
                </p>
            </LegalSection>

            <LegalSection id="california" title="8. California Residents (CCPA / CPRA)">
                <p>
                    California residents have additional rights under the California Consumer Privacy Act ("CCPA"), as amended by the California Privacy Rights Act ("CPRA"). In the past 12 months we have collected the categories of personal information listed in Section 2 (identifiers such as email; internet/network activity; approximate geolocation; commercial information about platform preference).
                </p>
                <p>
                    We do <strong>not</strong> sell personal information for money. We do <strong>not</strong> share personal information for cross-context behavioral advertising. Even so, California residents may submit a "Do Not Sell or Share My Personal Information" request at any time through our dedicated{" "}
                    <a href="/do-not-sell" className="text-[#0B4A34] underline">Do Not Sell or Share page</a>.
                </p>
                <p>
                    You may also designate an authorized agent to make a request on your behalf. We may require the agent to provide written authorization and may verify the agent's identity.
                </p>
            </LegalSection>

            <LegalSection id="retention" title="9. Data Retention">
                <p>
                    We retain personal information only for as long as needed to provide the Services or to comply with our legal obligations. Waitlist emails are retained until you unsubscribe or until 24 months after launch, whichever comes first.
                </p>
            </LegalSection>

            <LegalSection id="security" title="10. Security & Safe Environment">
                <p>
                    Protecting our users and providing a safe, secure environment is a first-class priority — not an afterthought. We use industry-standard technical and organizational measures to safeguard personal information, including:
                </p>
                <LegalList
                    items={[
                        "TLS 1.2+ encryption for all data in transit between your device and our servers.",
                        "Encryption at rest for our production databases.",
                        "Least-privilege access controls: only a small number of authorized personnel can access production data, and access is logged and audited.",
                        "Time-limited, rotated credentials for third-party integrations (email, hosting, analytics).",
                        "Regular dependency updates and security patching of our servers and application code.",
                        "Segregation of production, staging, and development environments so testing never touches real user data.",
                    ]}
                />
                <p>
                    No method of transmission or storage is 100% secure; we cannot guarantee absolute security. However, if we ever become aware of a data breach that affects your personal information, we will notify affected users and applicable regulators within the time frames required by law (typically within 72 hours for jurisdictions such as the EU/UK under GDPR).
                </p>
            </LegalSection>

            <LegalSection id="anti-deception" title="11. Preventing Deception">
                <p>
                    {ENTITY_NAME} is committed to honest, transparent representation of the Services. We will not, and will not knowingly permit third parties acting on our behalf to:
                </p>
                <LegalList
                    items={[
                        "Impersonate other people, companies, or entities, or misrepresent our affiliation with any organization.",
                        "Misrepresent the ownership, purpose, source, price, or functionality of the Site or the future App — including in marketing copy, screenshots, pricing tables, and forward-looking statements about coin bundles, power-ups, side bets, and match wagers.",
                        "Deceive users about how the game economy works, including the odds or expected outcomes of any AI-opponent match, the payout structure of match wagers where such features are permitted, or the effects of side bets on scoring.",
                        "Use social engineering, misleading UI patterns (\"dark patterns\"), or manipulative design to trick users into providing personal information, purchasing coin bundles, or joining match wagers.",
                        "Alter, redirect, or block content from third parties or the operating system to mislead users about its origin.",
                    ]}
                />
                <p>
                    In short: what you see is what you get. If you ever spot a claim on the Site or in the App that seems misleading, please report it to <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0B4A34] underline">{CONTACT_EMAIL}</a> and we will investigate and correct promptly.
                </p>
            </LegalSection>

            <LegalSection id="device-abuse" title="12. Device & Network Abuse Prohibitions">
                <p>
                    The Site, and the Golf Power Play mobile app when released, will operate within the security model of the user's device and platform. We commit that our Services will not:
                </p>
                <LegalList
                    items={[
                        "Interfere with, disrupt, damage, or gain unauthorized access to a device, network, server, or other infrastructure of any user or third party.",
                        "Install or execute unauthorized software, bootloaders, kernel extensions, or system modifications on a user's device.",
                        "Enable or facilitate the modification, replacement, or manipulation of another application's behavior, ads, or notifications, except with the user's explicit, informed consent.",
                        "Introduce malicious code (viruses, worms, trojans, spyware, rootkits, ransomware, cryptocurrency miners, or similar) to any device or system.",
                        "Access device APIs, sensors, or permissions beyond what is disclosed in this Privacy Policy and required to deliver the requested functionality — and only after the user grants permission via the operating system prompt.",
                        "Facilitate distributed denial-of-service (DDoS) attacks, botnets, spam, phishing campaigns, or credential stuffing.",
                        "Circumvent the security, sandboxing, or content protection features of the user's device or operating system.",
                    ]}
                />
                <p>
                    When the mobile app launches, any device permissions we request (for example, notifications for match invites or camera access if we ever add a profile-photo feature) will be requested only at the point of use, will be clearly explained, and can be revoked at any time through your device's Settings without losing access to core functionality.
                </p>
            </LegalSection>

            <LegalSection id="fair-play" title="13. Fair Play & Anti-Fraud">
                <p>
                    A safe environment for our users also means protecting them from fraud, cheating, and abusive behavior by other users. In the future App we plan to implement:
                </p>
                <LegalList
                    items={[
                        "Server-authoritative game logic — critical game state (deck order, opponent cards, side-bet resolution, final scores) is computed on our servers and cannot be manipulated by a modified client.",
                        "Bot / automation detection to prevent scripted agents from competing in real-coin match wagers.",
                        "Rate-limiting and anomaly detection on the waitlist, sign-up, and future purchase and wager endpoints to prevent enumeration and abuse.",
                        "Age-gating and identity verification for real-coin match wagers where legally required.",
                        "In-app tools for reporting cheating, harassment, or suspicious behavior by other players, with human review of reports.",
                        "Suspension or termination of accounts that violate our Terms of Service.",
                    ]}
                />
                <p>
                    Practice-mode games against AI opponents do not involve real-coin wagers and cannot affect any other user's balance or standing.
                </p>
            </LegalSection>

            <LegalSection id="children" title="14. Children's Privacy">
                <p>
                    The Services are not directed to children under 13, and we do not knowingly collect personal information from children under 13. If we learn that we have collected information from a child under 13 in violation of applicable law, we will delete it. The Golf Power Play app, when released, will be available only to users who are of legal age in their jurisdiction — and users may not participate in any match wager or side bet features until they meet the applicable minimum age for such features in their region.
                </p>
            </LegalSection>

            <LegalSection id="international" title="15. International Transfers">
                <p>
                    Golf Power Play is operated from the United States. If you access the Services from outside the United States, your information may be transferred to, stored, and processed in the United States or other countries where our service providers operate. Where required, we use appropriate safeguards (such as Standard Contractual Clauses) for such transfers.
                </p>
            </LegalSection>

            <LegalSection id="changes" title="16. Changes to This Policy">
                <p>
                    We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date at the top and, if changes are material, provide additional notice (such as a banner on the Site or an email to waitlist subscribers).
                </p>
            </LegalSection>

            <LegalSection id="contact" title="17. Contact Us">
                <p>
                    Questions, requests, or complaints about this Privacy Policy — including reports of suspected deception, device abuse, or safety concerns — can be sent to{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0B4A34] underline">{CONTACT_EMAIL}</a>. We take every safety and privacy report seriously and will respond within the time frames required by applicable law.
                </p>
            </LegalSection>
        </LegalLayout>
    );
}
